import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useAttachmentStaging } from '@/hooks/useAttachmentStaging';
import { uploadInvoiceAttachments, deleteInvoiceAttachment } from '../services/invoiceAttachments';
import { createPortal } from 'react-dom';
import { GenericForm, GenericFormField, RequiredMark, GenericSelect } from '@/styles/GenericForm';
import { GenericAttachmentsSection, GenericAttachment } from '@/styles/GenericAttachmentsSection';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Trash2 } from 'lucide-react';
import { CloseButton } from '@/styles/StandardButtons';
import { StandardButton, SecondaryButton } from '@/styles/StandardButtons';
import '@/design-system/typography.css';
import '@/design-system/forms.css';
import { Invoice, InvoiceItem, Project } from '../types';
import { InvoiceService } from '../services/invoiceService';
import { fetchEntity, fetchEntityList } from '@/lib/entity-api';
import { useProjectMembershipPrompt } from '@/hooks/useProjectMembershipPrompt';
import { useEntityComments } from '@/hooks/useEntityComments';
import { GenericCommentsSection, TEAM_CLIENT_VENDOR_AUDIENCES } from '@/styles/GenericCommentsSection';
import { PaymentHistorySection, toPaymentHistoryEntries, type PaymentHistoryEntry } from '@/components/shared/PaymentHistorySection';
import { InvoicePaymentCreateDialog } from './InvoicePaymentCreateDialog';
import { paymentFigures } from '../lib/invoice-payment-math';
import { useUser } from '@/contexts/UserContext';
import { useProject, getDefaultProjectIdForNewRecord } from '@/components/ProjectSelector';
import { fetchEntityAttachments } from '@/lib/entity-attachments';
import { getApiUrl } from '@/lib/api-config';
import { colors } from '@/design-system/colors';
import { Textarea } from '@/components/ui/textarea';


interface InvoiceItemType {
  id: string;
  description: string;
  quantity: number;
  unitPrice: number;
  total: number;
  orderNumber: number;
}

interface InvoiceFormProps {
  invoice: Invoice | null;
  projects: Project[];
  onClose: () => void;
  onSave: (invoiceData: any) => Promise<Invoice | undefined>;
  isOpen: boolean;
  /** Something the list shows changed outside this form's own Save (a payment was created from it): the caller reads the list again. */
  onChanged?: () => void;
}

export function InvoiceForm({
  invoice,
  projects,
  onClose,
  onSave,
  isOpen,
  onChanged,
}: InvoiceFormProps) {
  const { companyId, user } = useUser();
  const { ensureMembers, prompt: membershipPrompt } = useProjectMembershipPrompt();
  const { selectedProject } = useProject();
  const [formData, setFormData] = useState<Record<string, any>>({});
  // Comments belong to THIS invoice (entity_type 'invoice'), once it exists
  const { comments, addComment, deleteComment } = useEntityComments('invoice', invoice?.id || null);
  // The payments received on THIS invoice (invoice_payments with its id; a voided payment is not money received): read-only history
  const [payments, setPayments] = useState<PaymentHistoryEntry[]>([]);
  // "Create Payment": the invoice the payment form is open for (read fresh: its total and status decide what is left to pay)
  const [paymentFor, setPaymentFor] = useState<{ id: string; project_id: string; total_amount: number } | null>(null);
  const [preparingPayment, setPreparingPayment] = useState(false);
  const [invoiceItems, setInvoiceItems] = useState<InvoiceItemType[]>([]);
  const [taxPct, setTaxPct] = useState(0);
  const [markupPct, setMarkupPct] = useState(0);
  const [otherAmount, setOtherAmount] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const uploadedAttachmentsRef = useRef<GenericAttachment[]>([]);
  const uploadInvoiceAttachmentsFn = useCallback(async (id: string, files: File[]): Promise<GenericAttachment[]> => {
    if (!companyId || !user?.id) return [];
    return uploadInvoiceAttachments(id, files, companyId, user.id);
  }, [companyId, user]);
  const deleteInvoiceAttachmentFn = useCallback(async (attachmentId: string): Promise<boolean> => {
    if (!invoice?.id || !user || !companyId) return false;
    const attachment = uploadedAttachmentsRef.current.find(f => f.id === attachmentId);
    const storagePath = (attachment as any)?.storage_path || attachment?.file_url;
    return deleteInvoiceAttachment(attachmentId, companyId, user.id, storagePath);
  }, [invoice?.id, user, companyId]);
  const {
    pendingFiles,
    uploadedFiles: uploadedAttachments,
    setUploadedFiles: setUploadedAttachments,
    handleUpload: handleFileUpload,
    handleRemovePending: handleRemovePendingFile,
    handleDelete: handleDeleteAttachment,
  } = useAttachmentStaging<GenericAttachment>({
    entityId: invoice?.id,
    uploadFn: uploadInvoiceAttachmentsFn,
    deleteFn: deleteInvoiceAttachmentFn,
    onError: setError,
  });
  useEffect(() => {
    uploadedAttachmentsRef.current = uploadedAttachments;
  }, [uploadedAttachments]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const previousIsOpenRef = useRef(false);
  const initialValuesRef = useRef<Record<string, any>>({});
  const initialInvoiceItemsRef = useRef<InvoiceItemType[]>([]);
  const initialTaxPctRef = useRef<number>(0);
  const initialMarkupPctRef = useRef<number>(0);
  const initialOtherAmountRef = useRef<number>(0);
  const hasCapturedInitialValuesRef = useRef<boolean>(false);

  // Normalize function for consistent comparison
  const normalize = (obj: Record<string, any>): Record<string, string> => {
    return Object.fromEntries(
      Object.entries(obj).map(([k, v]) => [k, v == null ? '' : String(v).trim()])
    );
  };

  // Initialize form data
  const initialValues = useMemo(() => {
    if (invoice?.id) {
      return {
        number: invoice.number || '',
        project_id: invoice.project_id || '',
        name: invoice.name || '',
        issue_date: invoice.issue_date || '',
        due_date: invoice.due_date || '',
        type: invoice.type || 'Invoice',
        status: invoice.status || 'open',
        description: invoice.description || invoice.notes || '',
      };
    }
    return {
      number: '',
      project_id: getDefaultProjectIdForNewRecord(selectedProject),
      name: '',
      issue_date: new Date().toISOString().split('T')[0],
      due_date: '',
      type: 'Invoice',
      status: 'open',
      description: '',
    };
  }, [invoice, selectedProject]);

  // Note: Invoice items, tax, markup, and other amounts are now loaded in the main form open effect
  // This prevents duplicate state updates that cause false positives in unsaved changes detection

  // Capture initial values when form opens (like BillForm - capture immediately, no delays)
  useEffect(() => {
    if (isOpen && !previousIsOpenRef.current) {
      // Capture initial form data first, before setting state
      const currentInitialValues = invoice?.id ? {
        number: invoice.number || '',
        project_id: invoice.project_id || '',
        name: invoice.name || '',
        issue_date: invoice.issue_date || '',
        due_date: invoice.due_date || '',
        type: invoice.type || 'Invoice',
        status: invoice.status || 'open',
        description: invoice.description || invoice.notes || '',
      } : {
        number: '',
        project_id: getDefaultProjectIdForNewRecord(selectedProject),
        name: '',
        issue_date: new Date().toISOString().split('T')[0],
        due_date: '',
        type: 'Invoice',
        status: 'open',
        description: '',
      };

      // Normalize and capture initial values immediately (like BillForm)
      const normalizedInitial = normalize(currentInitialValues);
      initialValuesRef.current = normalizedInitial;
      setFormData(currentInitialValues);

      // Load invoice items - fetch directly if not in invoice object
      if (invoice?.id) {
        // Check if items are already in invoice object
        if ((invoice as any).items && Array.isArray((invoice as any).items)) {
          const items = (invoice as any).items.map((item: any) => ({
            id: item.id,
            description: item.description || '',
            quantity: item.quantity ?? 0,
            unitPrice: item.unit_price ?? 0,
            total: item.total_price ?? 0,
            orderNumber: item.order_number || 0,
          }));
          setInvoiceItems(items);
          initialInvoiceItemsRef.current = JSON.parse(JSON.stringify(items));
        } else if (companyId) {
          // Fetch items from database
          const loadInvoiceItems = async () => {
            try {
              const itemsData = (await fetchEntityList<any>('invoice_item', { filters: { invoice_id: invoice.id } }))
                .sort((a, b) => (a.order_number ?? 0) - (b.order_number ?? 0));

              if (itemsData && itemsData.length > 0) {
                const items = itemsData.map((item: any) => ({
                  id: item.id,
                  description: item.description || '',
                  quantity: item.quantity ?? 0,
                  unitPrice: item.unit_price ?? 0,
                  total: item.total_price ?? 0,
                  orderNumber: item.order_number || 0,
                }));
                setInvoiceItems(items);
                initialInvoiceItemsRef.current = JSON.parse(JSON.stringify(items));
              } else {
                const emptyItems: InvoiceItemType[] = [];
                setInvoiceItems(emptyItems);
                initialInvoiceItemsRef.current = JSON.parse(JSON.stringify(emptyItems));
              }
            } catch (error) {
              console.error('Error loading invoice items:', error);
              const emptyItems: InvoiceItemType[] = [];
              setInvoiceItems(emptyItems);
              initialInvoiceItemsRef.current = JSON.parse(JSON.stringify(emptyItems));
            }
          };
          loadInvoiceItems();
        }
      } else {
        const emptyItems: InvoiceItemType[] = [];
        setInvoiceItems(emptyItems);
        initialInvoiceItemsRef.current = JSON.parse(JSON.stringify(emptyItems));
      }

      // Load tax, markup, other and capture immediately (like BillForm)
      const initialTax = invoice?.tax_pct || 0;
      const initialMarkup = invoice?.markup_pct || 0;
      const initialOther = invoice?.other_amount || 0;
      setTaxPct(initialTax);
      setMarkupPct(initialMarkup);
      setOtherAmount(initialOther);
      initialTaxPctRef.current = initialTax;
      initialMarkupPctRef.current = initialMarkup;
      initialOtherAmountRef.current = initialOther;

      // Load attachments metadata (without signed URLs - lazy load those)
      if (invoice?.id) {
        const loadAttachments = async () => {
          try {
            setUploadedAttachments(await fetchEntityAttachments('invoice', invoice.id, { ascending: false }));
          } catch (error) {
            console.error('Error loading attachments:', error);
          }
        };

        loadAttachments();
      } else {
        setUploadedAttachments([]);
      }
      
      // Mark as captured immediately (like BillForm - no delays needed)
      hasCapturedInitialValuesRef.current = true;
    }
    
    // Reset capture flag when form closes (like BillForm)
    if (!isOpen && previousIsOpenRef.current) {
      hasCapturedInitialValuesRef.current = false;
    }
    
    previousIsOpenRef.current = isOpen;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Calculate totals
  const subtotal = invoiceItems.reduce((sum, it) => sum + it.total, 0);
  const taxAmount = (subtotal * taxPct) / 100;
  const markupAmount = (subtotal * markupPct) / 100;
  const total = subtotal + taxAmount + markupAmount + otherAmount;

  // Invoice fields (for reference, actual layout is in customLayout)
  const invoiceFields = useMemo<GenericFormField[]>(() => [
    {
      name: 'name',
      label: 'Invoice Name',
      type: 'text',
      row: 1,
      colSpan: 1,
      placeholder: 'Enter invoice name',
    },
    {
      name: 'project_id',
      label: 'Project',
      type: 'select',
      required: true,
      row: 1,
      colSpan: 1,
      options: projects.map(p => ({ label: p.name, value: p.id })),
      placeholder: 'Select',
    },
    {
      name: 'number',
      label: 'Invoice Number',
      type: 'text',
      row: 2,
      colSpan: 0.5, // 1/4 width
      placeholder: 'Enter invoice number',
    },
    {
      name: 'issue_date',
      label: 'Date',
      type: 'date',
      required: true,
      row: 2,
      colSpan: 0.5, // 1/4 width
    },
    {
      name: 'type',
      label: 'Type',
      type: 'select',
      row: 2,
      colSpan: 0.5, // 1/4 width
      options: [
        { label: 'Deposit', value: 'Deposit' },
        { label: 'Invoice', value: 'Invoice' },
        { label: 'Progress Invoice', value: 'Progress Invoice' },
      ],
      placeholder: 'Select',
    },
    {
      name: 'status',
      label: 'Status',
      type: 'select',
      row: 3,
      colSpan: 0.5, // 1/4 width
      options: [
        { label: 'Open', value: 'open' },
        { label: 'Sent', value: 'sent' },
        { label: 'Paid', value: 'paid' },
        { label: 'Partially Paid', value: 'partially_paid' },
        { label: 'Void', value: 'void' },
        { label: 'Draft', value: 'draft' },
      ],
      placeholder: 'Select',
    },
    {
      name: 'description',
      label: 'Description',
      type: 'textarea',
      row: 4,
      colSpan: 4, // Full width
      placeholder: 'Enter description',
    },
  ], [projects]);

  const handleFieldChange = (name: string, value: any, currentFormData: Record<string, any>) => {
    setFormData(currentFormData);
  };

  const addInvoiceItem = () => {
    const nextOrderNumber = invoiceItems.length > 0 ? Math.max(...invoiceItems.map(i => i.orderNumber)) + 1 : 1;
    const newItem: InvoiceItemType = {
      id: `temp-${crypto.randomUUID()}`,
      description: '',
      quantity: 1,
      unitPrice: 0,
      total: 0,
      orderNumber: nextOrderNumber,
    };
    setInvoiceItems(prev => [...prev, newItem]);
  };

  const handleInvoiceItemChange = (index: number, field: keyof InvoiceItemType, value: string | number) => {
    const newItems = [...invoiceItems];
    newItems[index] = {
      ...newItems[index],
      [field]: value,
    };
    if (field === 'quantity' || field === 'unitPrice') {
      newItems[index].total = Number((newItems[index].quantity * newItems[index].unitPrice).toFixed(2));
    }
    setInvoiceItems(newItems);
  };

  const removeInvoiceItem = async (id: string) => {
    if (!id.startsWith('temp-') && invoice?.id) {
      try {
        await InvoiceService.deleteInvoiceItem(id);
      } catch (e) {
        console.error('Error deleting invoice item:', e);
      }
    }
    setInvoiceItems(prev => prev.filter(it => it.id !== id));
  };


  const handleSubmit = async (data: Record<string, any>, skipClose: boolean = false) => {
    if (isSubmitting) return;
    setIsSubmitting(true);
    setError(null);

    try {
      if (!data.project_id) {
        setError('Please select a project');
        setIsSubmitting(false);
        return;
      }

      // Prepare invoice data
      let invoiceData: any;
      if (invoice?.id) {
        invoiceData = {
          id: invoice.id,
          project_id: data.project_id,
          number: data.number?.trim() || null,
          name: data.name || null,
          type: data.type || null,
          issue_date: data.issue_date || null,
          due_date: data.due_date || null,
          status: data.status,
          total_amount: total,
          notes: data.description || null,
          description: data.description || null,
          tax_pct: taxPct || 0,
          markup_pct: markupPct || 0,
          other_amount: otherAmount || 0,
        };
        console.log('🔍 [InvoiceForm] Updating invoice with ID:', invoice.id, 'Number:', data.number?.trim());
      } else {
        invoiceData = {
          company_id: companyId!,
          project_id: data.project_id,
          number: data.number?.trim() || null,
          name: data.name || null,
          type: data.type || 'Invoice',
          issue_date: data.issue_date,
          due_date: data.due_date || null,
          status: data.status || 'open',
          total_amount: total,
          notes: data.description || null,
          description: data.description || null,
          tax_pct: taxPct,
          markup_pct: markupPct,
          other_amount: otherAmount,
        };
      }

      const savedInvoice = await onSave({
        invoiceData,
        newAttachments: pendingFiles.map(file => ({
          file,
          name: file.name,
          size: file.size,
          type: file.type,
        })),
        removedAttachmentIds: [],
      });

      // Save invoice items
      if (savedInvoice && invoiceItems.length > 0) {
        for (const item of invoiceItems) {
          if (item.id.startsWith('temp-') || !item.id || item.id.length === 0) {
            await InvoiceService.createInvoiceItem({
              invoice_id: savedInvoice.id,
              description: item.description,
              quantity: item.quantity,
              unit_price: item.unitPrice,
              total_price: item.total,
              order_number: item.orderNumber,
            });
          } else {
            try {
              await InvoiceService.updateInvoiceItem({
                id: item.id,
                description: item.description,
                quantity: item.quantity,
                unit_price: item.unitPrice,
                total_price: item.total,
                order_number: item.orderNumber,
              });
            } catch (updateError: any) {
              await InvoiceService.createInvoiceItem({
                invoice_id: savedInvoice.id,
                description: item.description,
                quantity: item.quantity,
                unit_price: item.unitPrice,
                total_price: item.total,
                order_number: item.orderNumber,
              });
            }
          }
        }
      }

      // Only close if skipClose is false (normal save)
      if (!skipClose) {
        onClose();
      }

      // Return the saved invoice so it can be used by send/export functions
      return savedInvoice;
    } catch (err: any) {
      console.error('Error saving invoice:', err);
      setError(err?.message || 'Failed to save invoice');
      throw err; // Re-throw so send/export handlers can catch it
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleExportPDF = async () => {
    let invoiceId = invoice?.id;
    
    // If it's a new invoice, save it first
    if (!invoiceId) {
      try {
        const savedInvoice = await handleSubmit(formData, true); // Pass true to skipClose
        if (savedInvoice && (savedInvoice as any).id) {
          invoiceId = (savedInvoice as any).id;
        } else {
          setError('Please save the invoice first before exporting PDF');
          return;
        }
      } catch (err: any) {
        console.error('Error saving invoice before export:', err);
        setError(err?.message || 'Failed to save invoice');
        return;
      }
    }

    if (!invoiceId || !companyId) {
      setError('Cannot export PDF: Missing invoice or company information');
      return;
    }

    setIsGeneratingPDF(true);
    setError(null);

    try {
      // Send/Export separation (2026-09-14): Export PDF no longer goes
      // through document sharing (POST /share) -- it never needed a
      // persisted snapshot/share token, only the live document data to
      // print client-side. Opens the authenticated preview page, which
      // triggers window.print() itself (?print=1).
      window.open(`/documents/invoice/${invoiceId}/preview?print=1`, '_blank', 'noopener,noreferrer');
    } catch (err: any) {
      console.error('Error exporting PDF:', err);
      setError(err.message || 'Failed to export PDF');
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const handleSendToClient = async () => {
    let invoiceId = invoice?.id;
    
    // If it's a new invoice, save it first
    if (!invoiceId) {
      try {
        const savedInvoice = await handleSubmit(formData, true); // Pass true to skipClose
        if (savedInvoice && (savedInvoice as any).id) {
          invoiceId = (savedInvoice as any).id;
        } else {
          setError('Please save the invoice first before sending');
          return;
        }
      } catch (err: any) {
        console.error('Error saving invoice before send:', err);
        setError(err?.message || 'Failed to save invoice');
        return;
      }
    }

    if (!invoiceId || !companyId) {
      setError('Cannot send invoice: Missing invoice or company information');
      return;
    }

    setIsSendingEmail(true);
    setError(null);

    try {
      // The project's client reads the invoice as a member of the project: when they are not one yet, ask (once) before sending; cancelling sends nothing
      const projectId = formData.project_id;
      const project = projectId ? await fetchEntity<any>('project', projectId) : null;
      if (project?.client_id && !(await ensureMembers(projectId, [{ id: project.client_id, name: 'The client' }]))) {
        setError('The client must be a member of the project before the invoice can be sent.');
        return;
      }
      // Recipient (client) is resolved by the document definition from the
      // invoice's project itself -- same "one click, no email input" UX as
      // before. Status update to 'sent' happens server-side after a
      // successful send (document-sharing/types/invoice.js), so there's no
      // separate PUT to /api/entities/invoice here anymore.
      const apiUrl = getApiUrl(`api/documents/invoice/${invoiceId}/share`);
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({}),
      });

      if (!response.ok) {
        let errorMessage = 'Failed to send invoice';
        try {
          const errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch (e) {
          errorMessage = response.statusText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();
      if (result.success) {
        if (!result.emailed) {
          throw new Error('No client email found for this invoice');
        }
        alert('Invoice sent successfully to client!');
        onClose();
      } else {
        throw new Error(result.error || 'Failed to send invoice');
      }
    } catch (err: any) {
      console.error('Error sending invoice:', err);
      setError(err.message || 'Failed to send invoice to client');
      alert(err.message || 'Failed to send invoice to client');
    } finally {
      setIsSendingEmail(false);
    }
  };

  // Is there anything on screen that is not saved? (closing asks; so does Create Payment, which saves first)
  const hasUnsavedChanges = () => {
    // Only check for changes if we've captured initial values (like BidForm)
    if (!hasCapturedInitialValuesRef.current) return false;

    // Normalize current form data for comparison (like BidForm)
    const normalizedFormData = normalize(formData);
    const normalizedInitial = normalize(initialValuesRef.current);
    const formDataChanged = JSON.stringify(normalizedFormData) !== JSON.stringify(normalizedInitial);

    const normalizeInvoiceItems = (items: InvoiceItemType[]) => {
      return items.map(item => ({
        description: (item.description || '').trim(),
        quantity: item.quantity ?? 0,
        unitPrice: item.unitPrice ?? 0,
        total: item.total ?? 0,
        orderNumber: item.orderNumber ?? 0,
      }));
    };

    const currentInvoiceItemsNormalized = normalizeInvoiceItems(invoiceItems);
    const initialInvoiceItemsNormalized = normalizeInvoiceItems(initialInvoiceItemsRef.current);
    const invoiceItemsChanged = JSON.stringify(currentInvoiceItemsNormalized) !== JSON.stringify(initialInvoiceItemsNormalized);

    // Use strict comparison for numbers (like BidForm)
    const taxChanged = Number(taxPct) !== Number(initialTaxPctRef.current);
    const markupChanged = Number(markupPct) !== Number(initialMarkupPctRef.current);
    const otherChanged = Number(otherAmount) !== Number(initialOtherAmountRef.current);
    const hasPendingFiles = pendingFiles.length > 0;

    const isActuallyDirty = formDataChanged || invoiceItemsChanged || taxChanged || markupChanged || otherChanged || hasPendingFiles;

    return isActuallyDirty;
  };

  const handleCloseAttempt = () => {
    if (hasUnsavedChanges()) {
      if (window.confirm("Are you sure you want to lose all unsaved progress?")) {
        onClose();
      }
    } else {
      onClose();
    }
  };

  const loadPayments = useCallback(async () => {
    if (!invoice?.id) { setPayments([]); return; }
    try {
      const rows = await fetchEntityList<any>('invoice_payment', { filters: { invoice_id: invoice.id } });
      setPayments(toPaymentHistoryEntries(rows, 'invoice_id', invoice.id));
    } catch (err) {
      console.error('Error loading the payments of the invoice:', err);
    }
  }, [invoice?.id]);
  useEffect(() => { if (isOpen) void loadPayments(); }, [isOpen, loadPayments]);

  // "Create Payment": saves what is on screen first (the payment is for the invoice as it is now), reads the invoice fresh, and opens THE invoice payment form on it.
  // Nothing is created here and the status is not set: the server derives it from the payments. A void or fully paid invoice takes no new payment.
  const handleCreatePayment = async () => {
    if (!invoice?.id || preparingPayment) return;
    setPreparingPayment(true);
    setError(null);
    try {
      if (hasUnsavedChanges()) await handleSubmit(formData, true);
      const [fresh, payments] = await Promise.all([
        fetchEntity<any>('invoice', invoice.id),
        fetchEntityList<any>('invoice_payment', { filters: { invoice_id: invoice.id } }),
      ]);
      if (fresh.status === 'void') { setError('This invoice is void: it cannot receive payments.'); return; }
      if (paymentFigures({ totalAmount: fresh.total_amount, payments, amount: 0 }).leftToPayCents <= 0) {
        setError('This invoice is already paid in full: there is nothing left to pay.');
        return;
      }
      setPaymentFor({ id: fresh.id, project_id: fresh.project_id, total_amount: Number(fresh.total_amount) });
    } catch (err: any) {
      // (a failed save already put its own message on screen)
      if (!error) setError(err?.message || 'The payment could not be prepared');
    } finally {
      setPreparingPayment(false);
    }
  };

  // The payment exists: what it changed (the invoice's status, the list) is read again - no manual reload
  const handlePaymentCreated = async () => {
    setPaymentFor(null);
    void loadPayments();
    if (invoice?.id) {
      try {
        const fresh = await fetchEntity<any>('invoice', invoice.id);
        setFormData((previous) => ({ ...previous, status: fresh.status }));
        initialValuesRef.current = { ...initialValuesRef.current, status: String(fresh.status ?? '') };
      } catch (err) {
        console.error('Error reading the invoice after the payment:', err);
      }
    }
    onChanged?.();
  };

  // Custom layout for invoice form with line items
  const customLayout = (
    <div
      className="generic-form-overlay"
      style={{ top: 0, left: 0, right: 0, bottom: 0 }}
      onClick={handleCloseAttempt}
    >
      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit(formData, false); // Normal save, close after
        }}
        className="generic-form-container"
        style={{ width: 'var(--form-width-default)', maxHeight: 'var(--form-max-height-default)' }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div 
          className="generic-form-header generic-form-header-default"
          style={{ backgroundColor: colors.formHeaderFooter }}
        >
          <h2 className="font-form-title">
            {invoice?.id ? 'Edit Invoice' : 'Create Invoice'}
          </h2>
          <CloseButton onClick={handleCloseAttempt} />
        </div>

        {/* Body */}
        <div className="generic-form-body generic-form-body-default">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
              {error}
            </div>
          )}

          <div className="space-y-6">
            {/* First Row: Invoice Name and Project */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="name" className="font-form-label">Invoice Name</Label>
                <Input
                  id="name"
                  value={formData.name || ''}
                  onChange={(e) => {
                    const updated = { ...formData, name: e.target.value };
                    setFormData(updated);
                    handleFieldChange('name', e.target.value, updated);
                  }}
                  placeholder="Enter invoice name"
                  className="font-form-input generic-form-field"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="project_id" className="font-form-label">
                  Project<RequiredMark required />
                </Label>
                <GenericSelect
                  id="project_id"
                  value={formData.project_id || ''}
                  onValueChange={(value) => {
                    const updated = { ...formData, project_id: value };
                    setFormData(updated);
                    handleFieldChange('project_id', value, updated);
                  }}
                  placeholder="Select"
                  required
                  options={projects.map(p => ({ label: p.name, value: p.id }))}
                />
              </div>
            </div>

            {/* Second Row: Invoice Number, Date, Due Date */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="space-y-2">
                <Label htmlFor="number" className="font-form-label">Invoice Number</Label>
                <Input
                  id="number"
                  value={formData.number || ''}
                  onChange={(e) => {
                    const updated = { ...formData, number: e.target.value };
                    setFormData(updated);
                    handleFieldChange('number', e.target.value, updated);
                  }}
                  placeholder="Enter invoice number"
                  className="font-form-input generic-form-field"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="issue_date" className="font-form-label">Date</Label>
                <Input
                  id="issue_date"
                  type="date"
                  value={formData.issue_date || ''}
                  onChange={(e) => {
                    const updated = { ...formData, issue_date: e.target.value };
                    setFormData(updated);
                    handleFieldChange('issue_date', e.target.value, updated);
                  }}
                  className="font-form-input generic-form-field"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="due_date" className="font-form-label">Due Date</Label>
                <Input
                  id="due_date"
                  type="date"
                  value={formData.due_date || ''}
                  onChange={(e) => {
                    const updated = { ...formData, due_date: e.target.value };
                    setFormData(updated);
                    handleFieldChange('due_date', e.target.value, updated);
                  }}
                  className="font-form-input generic-form-field"
                />
              </div>
              <div></div> {/* Empty space */}
            </div>

            {/* Third Row: Type, Status, Amount */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="space-y-2">
                <Label htmlFor="type" className="font-form-label">Type</Label>
                <GenericSelect
                  id="type"
                  value={formData.type || 'Invoice'}
                  onValueChange={(value) => {
                    const updated = { ...formData, type: value };
                    setFormData(updated);
                    handleFieldChange('type', value, updated);
                  }}
                  placeholder="Select"
                  required
                  options={[
                    { label: 'Deposit', value: 'Deposit' },
                    { label: 'Invoice', value: 'Invoice' },
                    { label: 'Progress Invoice', value: 'Progress Invoice' },
                  ]}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="status" className="font-form-label">Status</Label>
                <GenericSelect
                  id="status"
                  value={formData.status || 'open'}
                  onValueChange={(value) => {
                    const updated = { ...formData, status: value };
                    setFormData(updated);
                    handleFieldChange('status', value, updated);
                  }}
                  placeholder="Select"
                  required
                  options={[
                    { label: 'Draft', value: 'draft' },
                    { label: 'Open', value: 'open' },
                    { label: 'Sent', value: 'sent' },
                    { label: 'Paid', value: 'paid' },
                    { label: 'Partially Paid', value: 'partially_paid' },
                    { label: 'Void', value: 'void' },
                  ]}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="amount" className="font-form-label">Amount ($)</Label>
                <Input
                  id="amount"
                  type="text"
                  className="font-form-input generic-form-field bg-gray-100 cursor-not-allowed"
                  value={total > 0 ? `$${total.toFixed(2)}` : '$0.00'}
                  placeholder="$0.00"
                  readOnly
                />
              </div>
              <div></div> {/* Empty space */}
            </div>

            {invoice?.id && <PaymentHistorySection payments={payments} title="Payments Received" testId="invoice-payments" />}

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="description" className="font-form-label">Description</Label>
              <Textarea
                id="description"
                value={formData.description || ''}
                onChange={(e) => {
                  const updated = { ...formData, description: e.target.value };
                  setFormData(updated);
                  handleFieldChange('description', e.target.value, updated);
                }}
                placeholder="Enter description"
                rows={3}
              />
            </div>

            {/* Line Items Section */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <Label className="font-bold">Line Items</Label>
                <StandardButton type="button" onClick={addInvoiceItem} customVariant="white" style={{ padding: '0.375rem 0.75rem', fontSize: '0.875rem' }}>
                  + Add Item
                </StandardButton>
              </div>
              <div className="border border-gray-200 rounded-md overflow-hidden">
                {/* Table Header */}
                <div className="grid grid-cols-5 gap-3 bg-gray-100 px-3 py-1.5 text-xs font-semibold text-gray-700 border-b border-gray-200">
                  <span className="col-span-2">Item</span>
                  <span className="text-right">Qty</span>
                  <span className="text-right">Unit Price ($)</span>
                  <div className="flex items-center justify-end gap-2">
                    <span>Total ($)</span>
                  </div>
                </div>
                {/* Table Body */}
                <div className="divide-y">
                  {invoiceItems.map((item, index) => (
                    <div
                      key={item.id}
                      className="grid grid-cols-5 gap-3 items-center px-3 py-1.5 bg-white hover:bg-gray-50 transition-colors"
                    >
                      <div className="col-span-2">
                        <Input
                          value={item.description}
                          onChange={(e) => handleInvoiceItemChange(index, 'description', e.target.value)}
                          placeholder="Item description"
                          className="h-7 text-xs border border-gray-200 rounded-md bg-white"
                        />
                      </div>
                      <div>
                        <Input
                          type="number"
                          value={item.quantity}
                          onChange={(e) => handleInvoiceItemChange(index, 'quantity', Number(e.target.value))}
                          placeholder="0"
                          className="h-7 text-xs border border-gray-200 rounded-md bg-white text-right [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        />
                      </div>
                      <div>
                        <Input
                          type="number"
                          value={item.unitPrice || ''}
                          onChange={(e) => handleInvoiceItemChange(index, 'unitPrice', Number(e.target.value))}
                          placeholder="$0.00"
                          className="h-7 text-xs border border-gray-200 rounded-md bg-white text-right [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        />
                      </div>
                      <div className="flex items-center justify-end gap-2">
                        <Input
                          value={item.total > 0 ? `$${item.total.toFixed(2)}` : '$0.00'}
                          readOnly
                          placeholder="$0.00"
                          className="bg-gray-50 text-right h-7 text-xs border border-gray-200 rounded-md"
                        />
                        {invoiceItems.length > 1 && (
                          <button
                            type="button"
                            onClick={() => removeInvoiceItem(item.id)}
                            className="text-gray-400 hover:text-destructive hover:bg-destructive/10 h-7 w-7 p-0 flex items-center justify-center"
                          >
                            <Trash2 className="h-3 w-3" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Totals Section */}
            <div className="flex justify-end">
              <div className="w-full md:w-1/3 space-y-2">
                <div className="grid grid-cols-[1fr,5rem,5rem] items-center text-sm">
                  <span className="justify-self-end">Subtotal</span>
                  <span></span>
                  <span className="text-right">${subtotal.toFixed(2)}</span>
                </div>

                <div className="grid grid-cols-[1fr,5rem,5rem] items-center gap-4 text-sm">
                  <label className="justify-self-end">Tax (%)</label>
                  <Input
                    type="number"
                    className="w-full h-8 p-1 text-sm text-right [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    value={taxPct || ''}
                    placeholder="0"
                    onChange={(e) => setTaxPct(Number(e.target.value))}
                  />
                  <span className="text-right">${taxAmount.toFixed(2)}</span>
                </div>

                <div className="grid grid-cols-[1fr,5rem,5rem] items-center gap-4 text-sm">
                  <label className="justify-self-end">Markup (%)</label>
                  <Input
                    type="number"
                    className="w-full h-8 p-1 text-sm text-right [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    value={markupPct || ''}
                    placeholder="0"
                    onChange={(e) => setMarkupPct(Number(e.target.value))}
                  />
                  <span className="text-right">${markupAmount.toFixed(2)}</span>
                </div>

                <div className="grid grid-cols-[1fr,5rem,5rem] items-center gap-4 text-sm">
                  <label className="justify-self-end">Other ($)</label>
                  <Input
                    type="number"
                    className="w-full h-8 p-1 text-sm text-right [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                    value={otherAmount || ''}
                    placeholder="$0.00"
                    onChange={(e) => setOtherAmount(Number(e.target.value))}
                  />
                  <span className="text-right">${otherAmount.toFixed(2)}</span>
                </div>

                <div className="grid grid-cols-[1fr,5rem,5rem] items-center text-sm font-semibold text-gray-900 border-t pt-2">
                  <span className="justify-self-end">Total</span>
                  <span></span>
                  <span className="text-right">${total.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Attachments Section */}
            <GenericAttachmentsSection
              attachments={uploadedAttachments}
              pendingFiles={pendingFiles}
              onUpload={handleFileUpload}
              onDelete={invoice?.id ? handleDeleteAttachment : undefined}
              onRemovePending={handleRemovePendingFile}
              projectId={formData.project_id || ''} mode={invoice?.id ? 'edit' : 'create'}
              disabled={false}
            />

            <GenericCommentsSection
              comments={comments}
              onAddComment={async (content, audience) => { await addComment(content, audience); }}
              onDeleteComment={async (commentId) => {
                if (!window.confirm('Are you sure you want to delete this comment?')) return;
                await deleteComment(commentId);
              }}
              mode={invoice?.id ? 'edit' : 'create'}
              audiences={TEAM_CLIENT_VENDOR_AUDIENCES}
              placeholder={invoice?.id ? 'Add a comment...' : 'Comments are available after creating the invoice'}
            />
          </div>
        </div>

        {/* Footer */}
        <div 
          className="generic-form-footer generic-form-footer-default"
          style={{ backgroundColor: colors.formHeaderFooter }}
        >
          <div className="flex gap-2">
            <StandardButton
              onClick={handleCloseAttempt}
              type="button"
              customVariant="white"
            >
              Cancel
            </StandardButton>
            <StandardButton
              type="button"
              onClick={handleSendToClient}
              customVariant="white"
              disabled={isSubmitting || isSendingEmail}
            >
              {isSendingEmail ? 'Sending...' : 'Send to Client'}
            </StandardButton>
            <StandardButton
              type="button"
              onClick={handleExportPDF}
              customVariant="white"
              disabled={isSubmitting || isGeneratingPDF}
            >
              {isGeneratingPDF ? 'Generating...' : 'Extract PDF'}
            </StandardButton>
            {invoice?.id && (
              <StandardButton
                type="button"
                onClick={handleCreatePayment}
                customVariant="white"
                disabled={isSubmitting || preparingPayment}
              >
                {preparingPayment ? 'Preparing...' : 'Create Payment'}
              </StandardButton>
            )}
            <SecondaryButton
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </SecondaryButton>
          </div>
        </div>
      </form>
    </div>
  );

  if (!isOpen) return null;

  // Render in a portal to ensure it's at the document body level
  // This ensures fixed positioning works correctly and covers the entire screen
  return (
    <>
      {membershipPrompt}
      {createPortal(customLayout, document.body)}
      {/* a sibling of the form (not inside it): a submit inside the payment form must never submit the invoice */}
      <InvoicePaymentCreateDialog isOpen={paymentFor !== null} invoice={paymentFor} onClose={() => setPaymentFor(null)} onCreated={() => { void handlePaymentCreated(); }} />
    </>
  );
}

